import { useQuery } from '@apollo/client';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useFormValues } from '@retainerclub/hooks';
import { GET_PRODUCTS_LAB_NOTE } from '@retainerclub/partner-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LoadingOrError } from '../../../../../shared';
import RetainerSelectionModal from './RetainerSelectionModal';

function AdditionalRetainerSelection({ partnerLocationId }) {
  const [open, setOpen] = useState(false);
  const [checkboxChange, setCheckboxChange] = useState(true);
  const [productId, setProductId] = useState(null);
  const [products, setProducts] = useState([]);
  const [archSelection, setArchSelection] = useState({
    upper: true,
    lower: true,
  });

  const { setValue } = useFormContext();
  const { productIds } = useFormValues();

  const productAdded = useMemo(
    () => productIds.includes(productId),
    [productId, productIds],
  );

  const { error, loading } = useQuery(GET_PRODUCTS_LAB_NOTE, {
    variables: {
      partnerLocationId,
      isRetainer: true,
    },
    onCompleted: (data) => {
      setProducts(data.productsLabNote);
    },
  });

  function handleSelection() {
    const selectedProduct = products.find((product) => {
      if (archSelection.upper && archSelection.lower) {
        return product.arches === 'both' && product.isEot === checkboxChange;
      }

      if (archSelection.upper && !archSelection.lower) {
        return product.arches === 'upper' && product.isEot === checkboxChange;
      }

      if (!archSelection.upper && archSelection.lower) {
        return product.arches === 'lower' && product.isEot === checkboxChange;
      }

      return false;
    });

    setProductId(selectedProduct.id);

    if (checkboxChange) {
      setValue('includeEot', checkboxChange);
    }

    setValue('productIds', [...productIds, selectedProduct.id]);

    setOpen(false);
  }

  function handleButton() {
    if (productId) {
      setValue(
        'productIds',
        productIds.filter((id) => id !== productId),
      );
      setProductId(null);
    }

    if (!productId) {
      setOpen(true);
    }
  }

  if (loading || error)
    return <LoadingOrError loading={loading} error={error} />;

  return (
    <>
      <div
        className={`relative flex basis-1/2 flex-col justify-between rounded-lg border ${productAdded ? 'border-rcprimary-400 bg-rcprimary-400/20' : 'border-black/20'} p-4`}
      >
        {productAdded && (
          <CheckCircleIcon className="text-rcprimary-400 absolute -right-6 -top-6 z-20 size-12" />
        )}
        <div className="relative">
          <div className="relative h-72 w-full overflow-hidden rounded-lg">
            <img
              src="https://cdn.retainerclub.com/images/products/retainer-pi.jpg"
              alt="Additional Retainers"
              className="size-full object-cover object-center"
            />
          </div>
          <div className="relative mt-4">
            <h3 className="text-sm font-medium text-gray-900">
              Retainers - Paid in Office
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Click here to choose upper/ lower arches or both - you will see
              this fee on your next invoice.
            </p>
          </div>
        </div>
        <div className="mt-6">
          <button
            type="button"
            onClick={handleButton}
            className={`${productAdded ? 'bg-rcprimary-300 hover:bg-rcprimary-400' : 'bg-rcprimary-400 hover:bg-gray-200'} relative flex w-full items-center justify-center rounded-md border border-transparent px-8 py-2 text-sm font-medium text-gray-900 `}
          >
            {productAdded ? 'Remove Selection' : 'Select Arches...'}
          </button>
        </div>
      </div>
      <RetainerSelectionModal
        open={open}
        setOpen={setOpen}
        archSelection={archSelection}
        setArchSelection={setArchSelection}
        setCheckboxChange={setCheckboxChange}
        checkboxChange={checkboxChange}
        handleSelection={() => handleSelection()}
      />
    </>
  );
}

AdditionalRetainerSelection.propTypes = {
  partnerLocationId: PropTypes.string.isRequired,
};

export default AdditionalRetainerSelection;
